import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Alert, Select, Space, Tabs, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  ContentBackground,
  ContentWrapper,
  ContentHeader,
  ContentBody,
} from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES, ROLES_PERMISSIONS } from '@/constants';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';
import FccsPanel from './_components/FccsPanel';
import CashFlowPanel from './_components/cash-flow/CashFlowPanel';
import InvestmentsPanel from './_components/investments/InvestmentsPanel';
import ParentPlPanel from './_components/parent-pl/ParentPlPanel';
import TaxReceivablePanel from './_components/tax-receivable/TaxReceivablePanel';
import { useGoToTab } from './_components/howToLinks';
import AbDataPanel from './_components/ab-data/AbDataPanel';
import AbPullsPanel from './_components/ab-data/AbPullsPanel';
import PackPanel from './_components/pack/PackPanel';
import MonthlyResultPanel from './_components/monthly-result/MonthlyResultPanel';
import IosPanel from './_components/ios/IosPanel';
import PartnerRequestsPanel from './_components/partner-requests/PartnerRequestsPanel';

const { Text } = Typography;

// The AB machine sends every night; a delivery older than this means it stopped
// (machine restarted, user signed out, script failing).
const STALE_AFTER_HOURS = 36;

/**
 * Client reports built from the AB Soft ledger the AB machine sends every
 * night. FCCS (the Telekom Srbija group consolidation upload) comes first.
 *
 * @returns {JSX.Element} The page
 */
function ClientReportsContent() {
  const { t } = useTranslation(['clientReports', 'menus']);
  const [companyId, setCompanyId] = useState(null);
  // Controlled so the "How to use" links can switch tabs.
  const [activeTab, setActiveTab] = useState('pack');
  useGoToTab(setActiveTab);
  // Who delivered what from which machine is platform plumbing: accountants see
  // their companies' data, operators also see the pull behind it.
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const isOperator =
    userRoles.includes(ROLES.techAdmin) || userRoles.includes(ROLES.superAdmin);

  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-companies'],
    queryFn: () => API.clientReports.getCompanies(),
  });

  const companies = data?.data || [];
  const selected = companies.find((c) => c._id === companyId) || null;

  useEffect(() => {
    if (!companyId && companies.length) setCompanyId(companies[0]._id);
  }, [companies, companyId]);

  const lastSync = selected?.lastSync;
  const stale =
    !lastSync ||
    dayjs().diff(dayjs(lastSync.createdAt), 'hour') > STALE_AFTER_HOURS;

  return (
    <RootLayout metadata={{ title: t('clientReports', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('clientReports', { ns: 'menus' })} />

            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  <p className="mb-4 text-sm opacity-70">{t('subtitle')}</p>

                  {!isLoading && !companies.length && (
                    <Alert type="info" showIcon message={t('noCompanies')} />
                  )}

                  {companies.length > 0 && (
                    <Space
                      direction="vertical"
                      size={4}
                      style={{ marginBottom: 12 }}
                    >
                      <Select
                        style={{ minWidth: 360 }}
                        value={companyId}
                        onChange={setCompanyId}
                        showSearch
                        optionFilterProp="label"
                        options={companies.map((c) => ({
                          value: c._id,
                          label: c.name,
                        }))}
                      />
                      <Text
                        type={stale ? 'danger' : 'secondary'}
                        style={{ fontSize: 12 }}
                      >
                        {lastSync
                          ? t('lastSync', {
                              received: dayjs(lastSync.createdAt).format(
                                'DD.MM.YYYY HH:mm'
                              ),
                              lastEntry: lastSync.lastEntryDate
                                ? dayjs(lastSync.lastEntryDate).format(
                                    'DD.MM.YYYY'
                                  )
                                : '—',
                              lines: lastSync.lineCount,
                              source: [
                                lastSync.abDatabase,
                                lastSync.abCompanyNo,
                              ]
                                .filter(Boolean)
                                .join(' / '),
                            })
                          : t('noSyncYet')}
                        {lastSync && stale && ` · ${t('stale')}`}
                      </Text>
                    </Space>
                  )}

                  {selected && (
                    <Tabs
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      items={[
                        {
                          key: 'pack',
                          label: t('pack.tab'),
                          children: <PackPanel companyId={selected._id} />,
                        },
                        {
                          key: 'monthlyResult',
                          label: t('monthlyResult.tab'),
                          children: (
                            <MonthlyResultPanel companyId={selected._id} />
                          ),
                        },
                        {
                          key: 'ios',
                          label: t('ios.tab'),
                          children: <IosPanel companyId={selected._id} />,
                        },
                        {
                          key: 'partnerRequests',
                          label: t('partnerRequests:tab'),
                          children: <PartnerRequestsPanel companyId={selected._id} />,
                        },
                        {
                          key: 'fccs',
                          label: t('fccs.tab'),
                          children: <FccsPanel companyId={selected._id} />,
                        },
                        {
                          key: 'cashFlow',
                          label: t('cashFlow.tab'),
                          children: <CashFlowPanel companyId={selected._id} />,
                        },
                        {
                          key: 'investments',
                          label: t('investments.tab'),
                          children: <InvestmentsPanel companyId={selected._id} />,
                        },
                        {
                          key: 'parentPl',
                          label: t('parentPl.tab'),
                          children: <ParentPlPanel companyId={selected._id} />,
                        },
                        {
                          key: 'taxReceivable',
                          label: t('taxReceivable.tab'),
                          children: (
                            <TaxReceivablePanel companyId={selected._id} />
                          ),
                        },
                        {
                          key: 'abData',
                          label: t('ab.tab'),
                          children: <AbDataPanel companyId={selected._id} />,
                        },
                        ...(isOperator
                          ? [
                              {
                                key: 'abPulls',
                                label: t('pulls.tab'),
                                children: <AbPullsPanel />,
                              },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

const ClientReportsPageWithAuth = withPrivateRoute(
  ClientReportsContent,
  ROLES_PERMISSIONS.CLIENT_REPORTS.VIEW
);
ClientReportsPageWithAuth.displayName = 'ClientReportsPage';

export default ClientReportsPageWithAuth;
